import { useDebounce } from '@/components/common/Debouncer';
import Export from '@/components/common/Export';
import { PreserveScroll } from '@/components/common/PreserveScroll';
import SimpleSearchField from '@/components/common/SimpleSearchField';
import { Line, SessionData, SessionTableRow } from '@/types/analytics';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  IconButton,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { FC, KeyboardEvent, useEffect, useState } from 'react';
import { useSessionStorage } from 'usehooks-ts';
import SessionFlowTable from './SessionFlowTable';
import { collapseAll, expandAll, expandRow } from './sessionFlowUtil';

type SessionFlowProps = {
  name: string;
  uuid: string;
  sessionData: SessionData;
  selectedRow: number;
  setSelectedRow: (step: number) => void;
  navigateToBuilder: (blockId: string) => void;
};

const SessionFlow: FC<SessionFlowProps> = ({
  name,
  uuid,
  sessionData,
  selectedRow,
  setSelectedRow,
  navigateToBuilder
}) => {
  const [currentUUID, setCurrentUUID] = useSessionStorage<string>('CurrentUUID', '');
  const [lines, setLines] = useSessionStorage<SessionTableRow[]>('SessionLines', []);
  const [filteredRows, setFilteredRows] = useState<number[]>([]);
  const [tasks, setTasks] = useSessionStorage<string[]>('SessionTasks', []);
  const [previousFilter, setPreviousFilter] = useState<{ id: string; value: string[] }>();
  const [search, setSearch] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [lockedSubroutines, setLockedSubroutines] = useSessionStorage<string[]>('SessionLockedSubs', []);
  const [markedLines, setMarkedLines] = useSessionStorage<number[]>('SessionMarkedLines', []);
  const [startScroll, setStartScroll] = useState<boolean>(false);
  const [openExport, setOpenExport] = useState<boolean>(false);

  const scrollRef = PreserveScroll('SessionScroll');

  const handleSetLines = (newLines: SessionTableRow[]) => setLines(newLines);

  const handleSelectedItem = (up: boolean, lines: number[]) => {
    const marker = selectedRow || 0;

    const expandedResult = handleExpandLines(marker, up, lines);

    expandedResult?.newLines && handleSetLines(expandedResult.newLines);
    expandedResult && setSelectedRow(expandedResult.newSelectedLine.row);
    setStartScroll(true);
  };

  const handleExpandLines = (marker: number, up: boolean, rows: number[]) => {
    if (up ? marker <= rows[0] : marker >= rows[rows.length - 1]) {
      marker = up ? rows[rows.length - 1] + 1 : 0;
    }

    const linesToSort = sessionData.lines.filter(
      (line) => !lockedSubroutines.includes(line.sub_name.trim()) && (up ? line.row < marker : line.row > marker)
    );
    const newSelectedLine = up
      ? linesToSort.findLast((line) => rows.includes(line.row))
      : linesToSort.find((line) => rows.includes(line.row));

    if (!newSelectedLine || newSelectedLine.row === marker) return;

    if (!lines.find((line) => line.row === newSelectedLine.row)) {
      const newLines = expandHiddenLines(newSelectedLine);
      return { newSelectedLine, newLines };
    }

    return { newSelectedLine, undefined };
  };

  const expandHiddenLines = (newSelectedMarker: Line) => {
    let result: SessionTableRow[] = [];
    const rowsToExpand = getHiddenRows(lines, newSelectedMarker.row, []) || [];
    rowsToExpand.forEach((row: number) => {
      const foundRow = sessionData.lines.find((line) => line.row === row);
      foundRow && result.push(...expandRow({ ...foundRow, expanded: 2 }, false, lines, lockedSubroutines, sessionData));
    });
    result = result.map((line) => (rowsToExpand.includes(line.row) ? { ...line, expanded: 2 } : line));
    return [...new Map(result.map((line) => [line.row, line])).values()];
  };

  const getHiddenRows = (newLines: SessionTableRow[], nextStep: number, result: number[]): number[] => {
    const hiddenLine = sessionData.lines.find((line) => line.row === nextStep);
    const currentSub = sessionData.subroutines.find((sub) => sub.sub_id === hiddenLine?.sub_id);
    if (!currentSub) return [];

    result.push(currentSub.first_row);
    if (lines.find((line) => line.row === currentSub.first_row)) {
      return result;
    } else {
      const nextRow = sessionData.subroutines.find(
        (sub) => sub.allrows.includes(currentSub.first_row) && sub.depth === currentSub.depth - 1
      )?.first_row;
      return getHiddenRows(newLines, nextRow || 0, result);
    }
  };

  const getSelectedLine = (marker: number) => {
    const currentIndex = filteredRows.findLastIndex((row) => row <= marker);

    return `${currentIndex + 1} of ${filteredRows.length}`;
  };

  const clearMarkers = () => {
    setMarkedLines([]);
  };

  const handleExpandAll = () => handleSetLines(expandAll(sessionData, lockedSubroutines));
  const handleCollapseAll = () => handleSetLines(collapseAll(sessionData));

  const scrollToTarget = (selectedRow: number) => {
    if (!scrollRef.current || !selectedRow) return;

    const container = scrollRef.current;
    const row = container.querySelector(`[data-row="${selectedRow}"]`);
    if (!row) return;

    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();

    const offset = rowRect.top - containerRect.top;

    container.scrollTo({
      top: container.scrollTop + offset - container.clientHeight / 2 + row.clientHeight / 2,
      behavior: 'smooth'
    });
  };

  const handleKeyPress = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!selectedRow) return;
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    event.preventDefault();

    if (event.key === 'ArrowUp') {
      if (selectedRow === lines[0].row) {
        setSelectedRow(lines[lines.length - 1].row);
        setStartScroll(true);
        return;
      }
      const newSelectedRow = lines.findLast(({ row }) => row <= selectedRow - 1)?.row;

      newSelectedRow && setSelectedRow(newSelectedRow);
    }
    if (event.key === 'ArrowDown') {
      if (selectedRow === lines[lines.length - 1].row) {
        setSelectedRow(1);
        setStartScroll(true);
        return;
      }
      const newSelectedRow = lines.find(({ row }) => row >= selectedRow + 1)?.row;

      newSelectedRow && setSelectedRow(newSelectedRow);
    }
  };

  useDebounce(() => setSearchValue(search), [search]);

  useEffect(() => {
    const filteredLines = sessionData.lines.filter(
      (line) =>
        !lockedSubroutines.includes(line.sub_name.trim()) &&
        (line.block_id.toString().includes(search.trim().toLowerCase()) ||
          line.block_type.toString().toLowerCase().includes(search.trim().toLowerCase()) ||
          line.step.toString().toLowerCase().includes(search.trim().toLowerCase()))
    );
    setFilteredRows(filteredLines.map((line) => line.row));
  }, [searchValue, lockedSubroutines]);

  useEffect(() => {
    if (selectedRow && !lines.find((line) => line.row === selectedRow)) setSelectedRow(0);
  }, [lines]);

  useEffect(() => {
    if (lines.length && uuid === currentUUID) return;

    setTasks(sessionData.tasks.map((task) => task.task));
    handleCollapseAll();
    setCurrentUUID(uuid);
  }, [sessionData]);

  useEffect(() => {
    scrollToTarget(selectedRow);
    setStartScroll(false);
  }, [startScroll]);

  return (
    <Box flexGrow={3} mr={1} mt={1} onKeyDown={(e) => handleKeyPress(e)}>
      <Stack direction='row' alignItems='center' justifyContent='space-between'>
        <Stack direction='row'>
          <SimpleSearchField
            size='small'
            search={search}
            setSearch={(newSearch) => setSearch(newSearch)}
            onKeyDown={(e) => e.key === 'Enter' && handleSelectedItem(false, filteredRows)}
          />
          {search && filteredRows.length > 0 && <Typography sx={{ px: 1 }}>{getSelectedLine(selectedRow)}</Typography>}
          <Box>
            <IconButton onClick={() => handleSelectedItem(true, filteredRows)}>
              <ArrowDropUpIcon />
            </IconButton>
            <IconButton onClick={() => handleSelectedItem(false, filteredRows)}>
              <ArrowDropDownIcon />
            </IconButton>
          </Box>
        </Stack>
        {sessionData.lines.length > 0 && (
          <Button variant='contained' onClick={() => setOpenExport(true)}>
            Export
          </Button>
        )}
      </Stack>
      <Stack direction='row' justifyContent='space-between' height='55px' my={1}>
        <Stack direction='row' alignItems='center'>
          <Autocomplete
            autoFocus
            multiple
            size='small'
            limitTags={2}
            disableCloseOnSelect
            value={tasks}
            groupBy={() => 'Task'}
            options={sessionData.tasks.map((task) => task.task)}
            onChange={(_, newValue) => {
              setTasks(newValue);
              setPreviousFilter(undefined);
            }}
            renderOption={(props, option, { selected }) => (
              <ListItem {...props} sx={{ '&:hover .filter-button': { visibility: 'visible' } }}>
                <Checkbox icon={<CheckBoxOutlineBlankIcon />} style={{ marginRight: 8 }} checked={selected} />
                <ListItemText>{option}</ListItemText>
                <Button
                  className='filter-button'
                  onClick={(e) => {
                    e.stopPropagation();

                    if (previousFilter?.id === option) {
                      setTasks(previousFilter.value);
                      setPreviousFilter(undefined);
                    } else {
                      setPreviousFilter({ id: option, value: tasks });
                      const filter = sessionData.tasks.filter((task) => task.task === option).map((task) => task.task);
                      setTasks(filter);
                    }
                  }}
                  size='small'
                  sx={{ visibility: previousFilter?.id === option ? 'visible' : 'hidden' }}
                >
                  {previousFilter?.id === option ? 'Undo' : 'Solo'}
                </Button>
              </ListItem>
            )}
            renderGroup={(params) => (
              <Box key={params.key}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    pl: 1
                  }}
                >
                  <Typography>{params.group}</Typography>
                  <Button
                    size='medium'
                    onClick={(e) => {
                      e.stopPropagation();
                      setTasks(sessionData.tasks.map((task) => task.task));
                      setPreviousFilter(undefined);
                    }}
                  >
                    Show All
                  </Button>
                </Box>
                {params.children}
              </Box>
            )}
            renderInput={(params) => <TextField {...params} placeholder='Filter Tasks' />}
            sx={{ width: '500px', mr: 2 }}
          />
          <Button size='small' variant='outlined' onClick={handleExpandAll} sx={{ mr: 1 }}>
            Expand All
          </Button>
          <Button size='small' variant='outlined' onClick={handleCollapseAll}>
            Collapse All
          </Button>
        </Stack>
        <Stack direction='row' alignItems='center'>
          <Stack display='flex' flexDirection='column'>
            <Button
              disabled={!markedLines.length}
              size='small'
              variant='outlined'
              fullWidth
              onClick={() => handleSelectedItem(true, markedLines)}
              startIcon={<ArrowDropUpIcon />}
              sx={{ justifyContent: 'flex-start' }}
            >
              Previous Marker
            </Button>
            <Button
              disabled={!markedLines.length}
              size='small'
              variant='outlined'
              fullWidth
              onClick={() => handleSelectedItem(false, markedLines)}
              startIcon={<ArrowDropDownIcon />}
              sx={{ justifyContent: 'flex-start' }}
            >
              Next Marker
            </Button>
          </Stack>
          <Button
            disabled={!markedLines.length}
            size='small'
            variant='outlined'
            sx={{ ml: 1, height: '100%', width: '125px' }}
            onClick={() => clearMarkers()}
          >
            Remove Markers
          </Button>
        </Stack>
      </Stack>
      <Box ref={scrollRef} height={'calc(100vh - 200px)'} overflow='auto'>
        <SessionFlowTable
          sessionData={sessionData}
          lines={lines.filter((line) => tasks.includes(line.task)).sort((a, b) => a.row - b.row)}
          setLines={(newLines) => handleSetLines(newLines)}
          searchText={searchValue}
          filteredTasks={tasks}
          markedLines={markedLines}
          setMarkedLines={(newLines) => setMarkedLines(newLines)}
          lockedSubroutines={lockedSubroutines}
          setLockedSubroutines={(newSubs) => setLockedSubroutines(newSubs)}
          selectedRow={selectedRow}
          setSelectedRow={(newRow) => setSelectedRow(newRow)}
          navigateToBuilder={navigateToBuilder}
        />
      </Box>
      <Export data={sessionData} name={name} open={openExport} onClose={() => setOpenExport(false)} />
    </Box>
  );
};

export default SessionFlow;
